import {useCallback, useEffect, useRef} from 'react';
import {Switch, Route, Redirect, useRouteMatch} from 'react-router-dom';
import queryString from 'query-string';
import {importAsyncComponent} from '@/utils/loadable';

import * as store from '@/store';
import {User} from '@/interface';
import {tocRouters} from '@/config/routers';
import {perfLogger} from '@/buried/performance';
import kuPiper from '@/middleware/kuPiper';
import {getRepositoryInfo, getDocumentInfo} from '@/services';
import FileTransfer from '@/domains/document/components/FileTransfer';
import UploadInput from '@/domains/document/components/UploadInput';
import TemplateDialog from '@/domains/document/components/TemplateDialog';
import ImportExcelDialog from '@/domains/document/components/ImportExcelDialog';
import PreToolbar from '@/components/useImageViewer/PreToolbar';
import RightButtomFixedBtns from '@/domains/user/RightButtomFixedBtns';

import DeleteDocumentConfirmDialog from '@/domains/space/components/docOperate/DeleteDocumentConfirmDialog';
import MoveDocumentConfirmDialog from '@/domains/space/components/docOperate/MoveDocumentConfirmDialog';
import ConvertToDirectory from '@/domains/space/components/docOperate/ConvertToDirectory';
import ConvertToDocDocument from '@/domains/space/components/docOperate/ConvertToDocDocument';
import CopyDocument from '@/domains/space/components/docOperate/CopyDocument';
import CreateDocument from '@/domains/space/components/docOperate/CreateDocument';
import CreateDirectory from '@/domains/space/components/docOperate/CreateDirectory';

import Repository from './RepositoryPage';
import ChangeTabIcon from '@/domains/user/components/changeTabIcon';
import {removeSearchParamNoRefresh} from '@/utils/urlUtils';
import {KNOWLEDGE_PATH_NAME} from '@/Constant';
import {initKuNotifyStatus} from '@/utils/document/kuNotifyStatus';
import {useHiddenRepo} from '@/hooks/kuSdk/hiddenRepo';

const NotFound = importAsyncComponent(() => import('@/pages/errorPage/404'));
const Error = importAsyncComponent(() => import('@/pages/errorPage/500'));
const Login = importAsyncComponent(() => import('./LoginPage'));
const HomePage = importAsyncComponent(() => import('./HomePage'));
const UnauthorizedPage = importAsyncComponent(() => import('./UnauthorizedPage'));

const routerList = [
    {
        path: tocRouters.Login.path,
        component: Login,
    },
    {
        path: tocRouters.Welcome.path,
        component: HomePage,
        exact: true,
    },
    {
        // mvp版本没有知识库其他页，先使用docPath精确匹配
        path: [tocRouters.Knowledge.docPath],
        component: Repository,
    },
    {
        path: [tocRouters.Error.notFound],
        component: NotFound,
    },
    {
        path: tocRouters.Error.serverError,
        component: Error,
    },
    {
        path: tocRouters.Unauthorized.path,
        component: UnauthorizedPage,
    },
];

let isFirstSpaNav = true;

interface IProps {
    user?: User;
}

const Entry: React.FC<IProps> = ({user}) => {
    const prevLocation = useRef<any>(null);
    const isRepoPage = useRouteMatch([tocRouters.Knowledge.repositoryPath]);
    const isForbiddenPage = location.pathname === '/403';
    const isLoginPage = location.pathname === '/login';


    // 如果是需要屏蔽的知识库 挑砖到屏蔽页面
    useHiddenRepo();

    const handleUrlQuery = useCallback((props: any) => {
        const searchParams = props?.location?.search || window.location.search;
        store.setUrlQuerySearch(queryString.parse(searchParams));
        if (new RegExp(/(\/knowledge)|(\/baike)|(\/department)|(\/home\/)/g).test(window.location.href)) {
            const {source} = queryString.parse(searchParams);
            store.setUrlQuery({source});
            removeSearchParamNoRefresh('source');
        } else {
            store.setUrlQuery({});
        }
        // 清除passport登录query
        removeSearchParamNoRefresh('bduss');
        removeSearchParamNoRefresh('errmsg');
        removeSearchParamNoRefresh('errno');
        removeSearchParamNoRefresh('ssnerror');
        removeSearchParamNoRefresh('traceid');
        removeSearchParamNoRefresh('stoken');
    }, []);

    const getInitData = useCallback(async props => {
        const prevLocationProps = prevLocation.current;
        prevLocation.current = props;
        const {spaceGuid, groupGuid, repositoryGuid, docGuid} = props.match.params;
        const pathConfig: string = props.match.path;
        if (!pathConfig.includes('/knowledge')) {
            perfLogger.docPagePaint.paintStop();
            isFirstSpaNav = false;
            return;
        }
        if (isFirstSpaNav) {
            isFirstSpaNav = false;
            return;
        }
        const isLocationChange = props.location.pathname !== prevLocationProps?.location?.pathname;
        if (!isLocationChange) {
            return;
        }
        const needRepository = ['/homeTag', '/square'].find(item => props.match.url.includes(item));
        const isDocPage = !!docGuid && !Object.values(KNOWLEDGE_PATH_NAME).includes(docGuid);
        // 知识库变更 & 非文档页面
        if ((isRepoPage && isRepoPage.isExact) || (isRepoPage && needRepository)) {
            getRepositoryInfo({
                spaceGuid,
                groupGuid,
                repositoryGuid,
            });
            return;
        }
        const prevDocGuid = prevLocationProps ? prevLocationProps.match.params.docGuid : '';
        const hasDocChanged = prevDocGuid !== docGuid;
        if (!hasDocChanged) {
            return;
        }
        if (isDocPage) {
            getDocumentInfo({
                spaceGuid,
                groupGuid,
                repositoryGuid,
                docGuid,
            });
        }
    }, [isRepoPage]);

    const routerRender = useCallback(
        (Component: any) => {
            return (props: any) => {
                handleUrlQuery(props);
                getInitData(props);

                return <Component {...props} />;
            };
        },
        [getInitData, handleUrlQuery]
    );


    kuPiper.runHook(kuPiper.hookNameMap.BeforeHomeRenderHook, {});

    useEffect(() => {
        kuPiper.runHook(kuPiper.hookNameMap.AfterHomeRenderHook, {});
    }, []);

    useEffect(() => {
        // 初始化监听知识库状态变更事件
        const destroy = initKuNotifyStatus();
        return () => {
            destroy();
        };
    }, []);


    return user ? (
        <>
            <Switch>
                {routerList.map(({path, component, exact}) => {
                    return <Route
                        key={path.toString()}
                        path={path}
                        exact={exact}
                        render={routerRender(component)}
                    />;
                })}
                <Route>
                    <Redirect to={tocRouters.Welcome.path} />
                </Route>
            </Switch>
            <CreateDocument />
            <CreateDirectory />
            <DeleteDocumentConfirmDialog />
            <MoveDocumentConfirmDialog />
            <ConvertToDirectory />
            <ConvertToDocDocument />
            <CopyDocument />
            <FileTransfer />
            <UploadInput />
            <TemplateDialog />
            <ImportExcelDialog />
            <PreToolbar />
            <RightButtomFixedBtns />
            <ChangeTabIcon />
        </>
    ) : (
        isForbiddenPage ? (
            <UnauthorizedPage />
        ) : isLoginPage ? (
            <Login />
        ) :
            // user信息没拿到时默认背景色，可由此判断是否缺失user信息导致白屏
            <div
                style={{
                    width: '100vw',
                    height: '100vh',
                    background: 'linear-gradient(to bottom, #f5f6fb, #fefeff) no-repeat',
                }}
            >
            </div>
    );
};

export default store.connect([store.KEYS.USER])(Entry);
